import React, { useState, useEffect } from 'react';
import { GlobeAltIcon, ClockIcon } from '@heroicons/react/24/outline';

interface TimezoneSelectorProps {
  value: string;
  onChange: (timezone: string) => void;
  disabled?: boolean;
}

// US Timezones with friendly names
const US_TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (New York)' },
  { value: 'America/Chicago', label: 'Central Time (Chicago)' },
  { value: 'America/Denver', label: 'Mountain Time (Denver)' },
  { value: 'America/Phoenix', label: 'Mountain Time - Arizona (Phoenix, no DST)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (Los Angeles)' },
  { value: 'America/Anchorage', label: 'Alaska Time (Anchorage)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii-Aleutian Time (Honolulu)' },
];

/**
 * Get timezone abbreviation (EST, PST, etc.)
 */
const getTimezoneAbbr = (timezone: string): string => {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    });
    const parts = formatter.formatToParts(new Date());
    const tzPart = parts.find((part) => part.type === 'timeZoneName');
    return tzPart?.value || timezone;
  } catch {
    return timezone;
  }
};

/**
 * Get current time in timezone
 */
const getCurrentTime = (timezone: string): string => {
  try {
    return new Date().toLocaleTimeString('en-US', {
      timeZone: timezone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return '--:--';
  }
};

/**
 * Get time until midnight in timezone
 */
const getTimeUntilMidnight = (timezone: string): string => {
  try {
    const now = new Date();
    const zonedNow = new Date(
      now.toLocaleString('en-US', { timeZone: timezone })
    );

    const tomorrow = new Date(zonedNow);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);

    const diff = tomorrow.getTime() - zonedNow.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    return `${hours}h ${minutes}m`;
  } catch {
    return '--h --m';
  }
};

export const TimezoneSelector: React.FC<TimezoneSelectorProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const [localTime, setLocalTime] = useState('');
  const [timeUntilReset, setTimeUntilReset] = useState('');

  // Update local time every second
  useEffect(() => {
    const updateTime = () => {
      setLocalTime(getCurrentTime(value));
      setTimeUntilReset(getTimeUntilMidnight(value));
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, [value]);

  const tzAbbr = getTimezoneAbbr(value);

  // Find the friendly label for the current timezone
  const currentTimezoneLabel = US_TIMEZONES.find(tz => tz.value === value)?.label || value.replace(/_/g, ' ');

  return (
    <div className="space-y-4">
      {/* Current timezone info */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
        <div className="flex items-center gap-3 mb-2">
          <GlobeAltIcon className="w-5 h-5 text-cyan-400" />
          <span className="text-sm font-medium text-slate-300">
            Current Timezone
          </span>
        </div>
        <div className="text-lg font-semibold text-white mb-1">
          {currentTimezoneLabel}
        </div>
        <div className="flex items-center gap-4 text-sm text-slate-400">
          <div className="flex items-center gap-1">
            <ClockIcon className="w-4 h-4" />
            <span>
              Local Time: {localTime} {tzAbbr}
            </span>
          </div>
          <div>Daily Reset: {timeUntilReset} remaining</div>
        </div>
      </div>

      {/* Timezone selector */}
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">
          Select Your Timezone
        </label>

        {/* Simple dropdown with US timezones */}
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {US_TIMEZONES.map((tz) => (
            <option key={tz.value} value={tz.value}>
              {tz.label}
            </option>
          ))}
        </select>
      </div>

      <p className="text-xs text-slate-400">
        Your timezone affects when daily quotas reset and how usage statistics
        are calculated.
      </p>
    </div>
  );
};
